import React, { useState, useEffect } from 'react';
import { FiDownload, FiCalendar, FiFilter, FiLock, FiPackage, FiShoppingCart, FiDollarSign } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorOwnerReports = () => {
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [reportType, setReportType] = useState('sales'); // 'sales', 'items', 'purchase', 'users', 'rooms', or 'banquet'
  const [salesReport, setSalesReport] = useState(null);
  const [itemsReport, setItemsReport] = useState(null);
  const [purchaseReport, setPurchaseReport] = useState(null);
  const [usersReport, setUsersReport] = useState(null);
  const [roomsReport, setRoomsReport] = useState(null);
  const [banquetReport, setBanquetReport] = useState(null);
  const [activeFilter, setActiveFilter] = useState('today');
  
  // Set initial date range to today
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  const [dateRange, setDateRange] = useState({
    startDate: todayStr,
    endDate: todayStr
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filters, setFilters] = useState({
    menuItemId: '',
    categoryId: '',
    subCategoryId: '',
    inventoryCategoryId: '',
    inventoryItemId: '',
    gstFilter: '', // 'withGST', 'withoutGST', or ''
    roomTypeFilter: 'all', // 'all', 'room', 'banquet'
    orderTypeFilter: 'all' // 'all', 'dine_in', 'delivery', 'pickup', 'vehicle'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    // Reset filters and active filter when report type changes
    setFilters({
      menuItemId: '',
      categoryId: '',
      subCategoryId: '',
      inventoryCategoryId: '',
      inventoryItemId: '',
      gstFilter: '',
      roomTypeFilter: 'all',
      orderTypeFilter: 'all'
    });
    setActiveFilter('today');
    setCurrentPage(1);
    
    // Reset date range to today when switching tabs
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setDateRange({ 
      startDate: todayStr, 
      endDate: todayStr 
    });
  }, [reportType]);

  useEffect(() => {
    if (selectedVendor) {
      if (reportType === 'sales') {
        fetchSalesReport();
      } else if (reportType === 'items') {
        fetchItemsReport();
      } else if (reportType === 'purchase') {
        fetchPurchaseReport();
      } else if (reportType === 'users') {
        fetchUsersReport();
      } else if (reportType === 'rooms') {
        fetchRoomsReport();
      } else if (reportType === 'banquet') {
        fetchBanquetReport();
      }
    }
  }, [dateRange, filters, reportType, selectedVendor]);

  useEffect(() => {
    if (selectedVendor) {
      fetchCategories();
      fetchMenuItems();
      fetchInventoryCategories();
      fetchInventoryItems();
    }
  }, [selectedVendor]);

  useEffect(() => {
    if (filters.categoryId) {
      fetchSubCategories(filters.categoryId);
    } else {
      setSubCategories([]);
    }
  }, [filters.categoryId]);

  useEffect(() => {
    if (selectedVendor) {
      fetchMenuItems(filters.categoryId, filters.subCategoryId);
    }
  }, [filters.categoryId, filters.subCategoryId, selectedVendor]);

  useEffect(() => {
    if (selectedVendor) {
      if (filters.inventoryCategoryId) {
        fetchInventoryItems(filters.inventoryCategoryId);
      } else {
        fetchInventoryItems();
      }
    }
  }, [filters.inventoryCategoryId, selectedVendor]);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/vendor-owner/vendors');
      if (response.data.success) {
        setVendors(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedVendor(response.data.data[0]._id);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch vendors');
    }
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor 
      };
      if (filters.menuItemId) params.menuItemId = filters.menuItemId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.subCategoryId) params.subCategoryId = filters.subCategoryId;
      if (filters.gstFilter) params.gstFilter = filters.gstFilter;
      // Always send orderType parameter
      if (filters.orderTypeFilter === 'all') {
        params.orderType = 'all'; // Send 'all' for all order types
      } else if (filters.orderTypeFilter) {
        params.orderType = filters.orderTypeFilter; // Send 'dine_in', 'delivery', 'pickup', or 'vehicle'
      }

      const response = await api.get('/vendor-owner/reports/sales', { params });
      setSalesReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const fetchItemsReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor 
      };
      // Only send category and subcategory filters for items report
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.subCategoryId) params.subCategoryId = filters.subCategoryId;
      // Don't send menuItemId or gstFilter for items report

      const response = await api.get('/vendor-owner/reports/items', { params });
      setItemsReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch items report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor,
        page: currentPage,
        limit: itemsPerPage
      };
      // Use inventory category and item filters for purchase report
      if (filters.inventoryCategoryId) params.categoryId = filters.inventoryCategoryId;
      if (filters.inventoryItemId) params.inventoryItemId = filters.inventoryItemId;

      const response = await api.get('/vendor-owner/reports/purchase', { params });
      setPurchaseReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch purchase report');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor 
      };
      const response = await api.get('/vendor-owner/reports/users', { params });
      setUsersReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch users report');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor 
      };
      // Always send roomType parameter
      if (filters.roomTypeFilter === 'all') {
        params.roomType = 'all'; // Send 'all' for all room types
      } else if (filters.roomTypeFilter) {
        params.roomType = filters.roomTypeFilter; // Send 'room' or 'banquet'
      }
      const response = await api.get('/vendor-owner/reports/rooms', { params });
      setRoomsReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch rooms report');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanquetReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        vendorId: selectedVendor 
      };
      const response = await api.get('/vendor-owner/reports/banquet', { params });
      setBanquetReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch banquet report');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/vendor-owner/categories/${selectedVendor}`);
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await api.get(`/vendor-owner/sub-categories/${selectedVendor}`);
      if (response.data.success) {
        const filtered = response.data.data.filter(sub => sub.categoryId._id === categoryId);
        setSubCategories(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch subcategories', categoryId);
    }
  };

  const fetchMenuItems = async (categoryId, subCategoryId) => {
    try {
      const response = await api.get(`/vendor-owner/menu-items/${selectedVendor}`);
      if (response.data.success) {
        let filtered = response.data.data;
        if (categoryId) {
          filtered = filtered.filter(item => item.categoryId?._id === categoryId);
        }
        if (subCategoryId) {
          filtered = filtered.filter(item => item.subCategoryId?._id === subCategoryId);
        }
        setMenuItems(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch menu items');
    }
  };

  const fetchInventoryCategories = async () => {
    try {
      const response = await api.get(`/vendor-owner/inventory-categories/${selectedVendor}`);
      if (response.data.success) {
        setInventoryCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch inventory categories');
    }
  };

  const fetchInventoryItems = async (inventoryCategoryId) => {
    try {
      const response = await api.get(`/vendor-owner/inventory-items/${selectedVendor}`);
      if (response.data.success) {
        let filtered = response.data.data;
        if (inventoryCategoryId) {
          filtered = filtered.filter(item => item.categoryId?._id === inventoryCategoryId);
        }
        setInventoryItems(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch inventory items');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      menuItemId: '',
      categoryId: '',
      subCategoryId: '',
      inventoryCategoryId: '',
      inventoryItemId: '',
      gstFilter: '',
      roomTypeFilter: 'all',
      orderTypeFilter: 'all'
    });
  };

  const hasActiveFilters = filters.menuItemId || filters.categoryId || filters.subCategoryId || filters.inventoryCategoryId || filters.inventoryItemId || filters.gstFilter || (filters.roomTypeFilter && filters.roomTypeFilter !== 'all') || (filters.orderTypeFilter && filters.orderTypeFilter !== 'all');

  const handleQuickFilter = (filter) => {
    setActiveFilter(filter);
    const today = new Date();
    let startDate, endDate;

    switch(filter) {
      case 'today':
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        startDate = last7.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      default:
        startDate = endDate = today.toISOString().split('T')[0];
    }

    setDateRange({ startDate, endDate });
  };

  const downloadReport = () => {
    if (reportType === 'sales') {
      downloadSalesReport();
    } else if (reportType === 'items') {
      downloadItemsReport();
    } else if (reportType === 'purchase') {
      downloadPurchaseReport();
    } else if (reportType === 'users') {
      downloadUsersReport();
    } else if (reportType === 'rooms') {
      downloadRoomsReport();
    } else if (reportType === 'banquet') {
      downloadBanquetReport();
    }
  };

  const downloadSalesReport = () => {
    if (!salesReport) return;
    
    const csvContent = [
      ['Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Completed Orders', salesReport.completedOrders],
      ['Total Sales', `${salesReport.totalSales}`],
      ['Cash Sales', `${salesReport.totalCashSales || 0}`],
      ['Online Sales', `${salesReport.totalOnlineSales || 0}`],
      ['Total GST', `${salesReport.totalGST}`],
      [],
      ['Orders by Type'],
      ['Dine-in', salesReport.ordersByType['dine-in']],
      ['Delivery', salesReport.ordersByType['delivery']],
      ['Pickup', salesReport.ordersByType['pickup']],
      [],
      ['Order Details'],
      ['Order Number', 'Date', 'Customer', 'Type', 'Customer GST Number', 'Subtotal','Discount','GST Amount','Total Amount','Status']
    ];

    salesReport.orders.forEach(order => {
      csvContent.push([
        order.orderNumber,
        new Date(order.createdAt).toLocaleDateString(),
        order.customerId?.fullName || 'Guest',
        order.orderType,
        order.gstNumber || 'N/A',
        order.pricing?.subtotal || 0,
        order.pricing?.discount || 0,
        order.pricing?.gstAmount || 0,
        order.pricing?.total || 0,
        order.status
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Sales report downloaded');
  };

  const downloadItemsReport = () => {
    if (!itemsReport) return;
    
    const csvContent = [
      ['Item-wise Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Items Sold', itemsReport.totalItems],
      ['Total Quantity', itemsReport.totalQuantity],
      [],
      ['Item Details'],
      ['Item Name', 'Category', 'Sub Category', 'Quantity Sold', 'Price', 'Total Revenue', '% of Total']
    ];

    itemsReport.items.forEach(item => {
      csvContent.push([
        item.itemName,
        item.category || 'N/A',
        item.subCategory || 'N/A',
        item.quantitySold,
        `${item.price}`,
        `${item.totalRevenue}`,
        `${item.percentageOfTotal.toFixed(1)}%`
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `items-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Items report downloaded');
  };

  const downloadPurchaseReport = () => {
    if (!purchaseReport) return;
    
    const csvContent = [
      ['Purchase Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Purchases', `${purchaseReport.totalPurchases}`],
      ['Total Amount', `${purchaseReport.totalAmount}`],
      [],
      ['Purchase Details'],
      ['Date', 'Item Name', 'Category', 'Quantity', 'Cost Per Unit', 'Total Cost', 'Notes']
    ];

    purchaseReport.purchases.forEach(purchase => {
      csvContent.push([
        new Date(purchase.createdAt).toLocaleDateString(),
        purchase.itemId?.name || 'N/A',
        purchase.itemId?.categoryId?.name || 'N/A',
        purchase.quantity,
        `${purchase.costPerUnit}`,
        `${purchase.totalCost}`,
        purchase.notes || '-'
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `purchase-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Purchase report downloaded');
  };

  const downloadUsersReport = () => {
    if (!usersReport) return;
    
    const csvContent = [
      ['User Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Customers', usersReport.totalCustomers],
      ['Total Orders', usersReport.totalOrders],
      ['Total Revenue', `${usersReport.totalRevenue}`],
      ['Average Revenue Per Customer', `${usersReport.averageRevenuePerCustomer?.toFixed(2)}`],
      [],
      ['User Details'],
      ['Customer Name', 'Phone', 'Total Orders', 'Total Revenue', 'Avg Order Value', 'Dine-in', 'Delivery', 'Pickup', 'Vehicle', 'Total Visits', 'First Visit', 'Last Visit']
    ];

    usersReport.users.forEach(user => {
      csvContent.push([
        user.customerName,
        user.customerPhone,
        user.totalOrders,
        `${user.totalRevenue}`,
        `${user.averageOrderValue?.toFixed(2)}`,
        user.dineInCount,
        user.deliveryCount,
        user.pickupCount,
        user.vehicleCount || 0,
        user.totalVisits,
        new Date(user.firstVisit).toLocaleDateString(),
        new Date(user.lastVisit).toLocaleDateString()
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Users report downloaded');
  };

  const downloadRoomsReport = () => {
    if (!roomsReport) return;
    
    const csvContent = [
      ['Room Checkout Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Bookings Rooms', roomsReport.totalBookings || 0],
      ['Total Checked In Rooms', roomsReport.totalCheckedInRooms || 0],
      ['Total Checked Out Rooms', roomsReport.totalCheckedOutRooms || 0],
      ['Total booked Rooms', roomsReport.totalBookedRooms || 0],
      ['Total Cancelled Rooms', roomsReport.totalCancelledRooms || 0],
      ['Room Revenue', (roomsReport.totalRoomRevenue || 0).toFixed(2)],
      ['GST Amount', (roomsReport.totalGSTAmount || 0).toFixed(2)],
      // ['Other Amount', (roomsReport.totalOtherAmount || 0).toFixed(2)],
      ['Total Revenue', (roomsReport.totalRevenue || 0).toFixed(2)],
      ['Average Stay Duration', `${roomsReport.averageStayDays} Days`],
      [],
      ['Booking Details'],
      ['Room Details', 'Guest & Status', 'Stay Period', 'Room Amount', 'GST Amount', 'Adjustments', 'Final Amount', 'Paid Amount', 'Cash', 'Card', 'UPI', 'Payment Status']
    ];

    roomsReport.roomDetails.forEach(room => {
      csvContent.push([
        `${room.roomNumber} - ${room.roomType || 'Standard'}`,
        `${room.guestName || '-'} (${room.status?.replace('_', ' ') || 'checked_out'})`,
        `${room.checkIn ? new Date(room.checkIn).toLocaleDateString() : '-'} to ${room.checkOut ? new Date(room.checkOut).toLocaleDateString() : '-'}`,
        (room.totalAmount || 0).toFixed(2),
        (room.gstAmount || 0).toFixed(2),
        (room.otherAmount || 0).toFixed(2),
        `"Tip: ${(room.tipAmount || 0).toFixed(2)}, Discount: -${(room.discountAmount || 0).toFixed(2)}"`,
        (room.totalFinalAmount || 0).toFixed(2),
        (room.paidAmount || 0).toFixed(2),
        (room.paymentModeBreakdown?.cash || 0).toFixed(2),
        (room.paymentModeBreakdown?.card || 0).toFixed(2),
        (room.paymentModeBreakdown?.upi || 0).toFixed(2),
        room.paymentStatus || 'pending'
      ]);
    });

    const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `room-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Room report downloaded');
  };

  const downloadBanquetReport = () => {
    if (!banquetReport) return;
    
    const csvContent = [
      ['Banquet Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Packages', banquetReport.totalPackages || 0],
      ['Total Revenue', (banquetReport.totalRevenue || 0).toFixed(2)],
      ['Average GST Percentage', `${banquetReport.avgGstPercentage || 0}%`],
      ['Average Price Per Pack', Number(banquetReport.avgPricePerPack || 0).toFixed(2)],
      [],
      ['Banquet Details'],
      ['Bill No', 'Customer Name', 'GST Number', 'Banquet Name', 'Packages', 'Price/Pack', 'Subtotal', 'GST %', 'GST Amount', 'Discount', 'Total', 'Date']
    ];

    banquetReport.banquets?.forEach(banquet => {
      csvContent.push([
        banquet.billNo || 'N/A',
        banquet.customerName || 'N/A',
        banquet.gstNumber || 'N/A',
        banquet.banquetName || 'N/A',
        banquet.noOfPacks || 0,
        (banquet.pricePerPack || 0).toFixed(2),
        (banquet.subtotal || 0).toFixed(2),
        `${banquet.gstPercentage || 0}%`,
        (banquet.gstAmount || 0).toFixed(2),
        (banquet.discountAmount || 0).toFixed(2),
        (banquet.total || 0).toFixed(2),
        new Date(banquet.createdAt).toLocaleDateString('en-IN')
      ]);
    });

    const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `banquet-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Banquet report downloaded');
  };

  if (loading && !selectedVendor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8">
          <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
            <FiFilter className="text-yellow-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Vendors Found</h2>
          <p className="text-gray-600">You don't have any vendors assigned to you yet.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-4 md:p-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Owner Reports</h1>
              <p className="text-gray-600">Analyze sales performance and analytics across your vendors</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-700">Select Vendor:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 min-w-[200px]"
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                >
                  <option value="">Choose a vendor...</option>
                  {vendors.map(vendor => (
                    <option key={vendor._id} value={vendor._id}>
                      {vendor.restaurantDetails?.restaurantName || vendor.personalDetails?.name || 'Unknown Vendor'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-transparent">Action:</label>
                <button 
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                  onClick={downloadReport}
                  disabled={!selectedVendor}
                >
                  <FiDownload className="text-lg" />
                  <span>Download Report</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {!selectedVendor ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiFilter className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Vendor</h3>
            <p className="text-gray-600">Please select a vendor to view their reports</p>
          </div>
        ) : (
          <>
            {/* Report Type Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                <button
                  onClick={() => setReportType('sales')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'sales' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">📈</span>
                  <span className="text-xs">Vendor Sales</span>
                </button>
                <button
                  onClick={() => setReportType('rooms')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'rooms' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">🏠</span>
                  <span className="text-xs">Room Sales</span>
                </button>
                <button
                  onClick={() => setReportType('banquet')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'banquet' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">🎊</span>
                  <span className="text-xs">Banquet Sales</span>
                </button>
                <button
                  onClick={() => setReportType('items')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'items' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">🍽️</span>
                  <span className="text-xs">Item-wise Sales</span>
                </button>
                <button
                  onClick={() => setReportType('purchase')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'purchase' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">💰</span>
                  <span className="text-xs">Purchase</span>
                </button>
                <button
                  onClick={() => setReportType('users')}
                  className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                    reportType === 'users' 
                      ? 'border-red-500 text-red-600 bg-white' 
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <span className="text-base">👥</span>
                  <span className="text-xs">Users</span>
                </button>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col lg:flex-row gap-3 items-center flex-wrap">
                <button 
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    activeFilter === 'today' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => handleQuickFilter('today')}
                >
                  Today
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    activeFilter === 'yesterday' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => handleQuickFilter('yesterday')}
                >
                  Yesterday
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    activeFilter === 'last7' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => handleQuickFilter('last7')}
                >
                  Last 7 Days
                </button>
                <button 
                  className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    activeFilter === 'thisMonth' 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  onClick={() => handleQuickFilter('thisMonth')}
                >
                  This Month
                </button>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={dateRange.startDate}
                    onChange={(e) => {
                      setDateRange({ ...dateRange, startDate: e.target.value });
                      setActiveFilter('custom');
                    }}
                    placeholder="Start Date"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={dateRange.endDate}
                    onChange={(e) => {
                      setDateRange({ ...dateRange, endDate: e.target.value });
                      setActiveFilter('custom');
                    }}
                    placeholder="End Date"
                  />
                </div>
            {
              reportType === 'rooms' && (
                <div>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={filters.roomTypeFilter}
                    onChange={(e) => handleFilterChange('roomTypeFilter', e.target.value)}
                  >
                    <option value="all">All Rooms</option>
                    <option value="room">Rooms Only</option>
                    <option value="banquet">Banquet Only</option>
                  </select>
                </div>
              )
            }
            {
              (reportType !== "sales" && reportType !== "users" && reportType !== "rooms" && reportType !== "banquet") && (
                    <button 
                      className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                        showFilters 
                          ? 'bg-red-100 text-red-700 border-2 border-red-200' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                      }`}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FiFilter className="text-lg" />
                      <span>{showFilters ? 'Hide' : 'Show'} Advanced Filters</span>
                      {hasActiveFilters && (
                        <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse">
                          {Object.values(filters).filter(v => v).length}
                        </span>
                      )}
                    </button>
                  )
                }
                {
                  reportType === 'sales' && (
                    <div className="flex gap-2">
                      <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.gstFilter}
                        onChange={(e) => handleFilterChange('gstFilter', e.target.value)}
                      >
                        <option value="">All Orders</option>
                        <option value="withGST">With Customer GST</option>
                        <option value="withoutGST">Without Customer GST</option>
                      </select>
                      <select
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.orderTypeFilter}
                        onChange={(e) => handleFilterChange('orderTypeFilter', e.target.value)}
                      >
                        <option value="all">All Order Types</option>
                        <option value="dine-in">Dine In</option>
                        <option value="delivery">Delivery</option>
                        <option value="pickup">Pickup</option>
                        <option value="vehicle">Vehicle</option>
                      </select>
                    </div>
                  )
                }
              </div>

              {/* Advanced Filters */}
              {showFilters && reportType !== 'sales' && reportType !== 'users' && reportType !== "rooms" && (
                <div className="bg-gray-50 rounded-lg p-4 mt-4 border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {reportType === 'purchase' ? (
                      <>
                        {/* Purchase Report - Inventory Category */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                            value={filters.inventoryCategoryId}
                            onChange={(e) => {
                              handleFilterChange('inventoryCategoryId', e.target.value);
                              handleFilterChange('inventoryItemId', '');
                            }}
                          >
                            <option value="">All Categories</option>
                            {inventoryCategories.map(cat => (
                              <option key={cat._id} value={cat._id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Purchase Report - Inventory Item */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Item</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                            value={filters.inventoryItemId}
                            onChange={(e) => handleFilterChange('inventoryItemId', e.target.value)}
                          >
                            <option value="">All Items</option>
                            {inventoryItems.map(item => (
                              <option key={item._id} value={item._id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Items Report - Menu Category */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                            value={filters.categoryId}
                            onChange={(e) => {
                              handleFilterChange('categoryId', e.target.value);
                              handleFilterChange('subCategoryId', '');
                            }}
                          >
                            <option value="">All Categories</option>
                            {categories.map(cat => (
                              <option key={cat._id} value={cat._id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Items Report - Sub Category */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Sub Category</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            value={filters.subCategoryId}
                            onChange={(e) => handleFilterChange('subCategoryId', e.target.value)}
                            disabled={!filters.categoryId}
                          >
                            <option value="">All Sub Categories</option>
                            {subCategories.map(sub => (
                              <option key={sub._id} value={sub._id}>{sub.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Items Report - Menu Item */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Menu Item</label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                            value={filters.menuItemId}
                            onChange={(e) => handleFilterChange('menuItemId', e.target.value)}
                          >
                            <option value="">All Menu Items</option>
                            {menuItems.map(item => (
                              <option key={item._id} value={item._id}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {hasActiveFilters && (
                      <div className="mt-4 flex justify-end">
                        <button 
                          className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors duration-200" 
                          onClick={clearFilters}
                        >
                          Clear All Filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Summary Stats */}
            {reportType === 'sales' && (!salesReport || salesReport.totalOrders === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
                <p className="text-gray-600 mb-6">No orders found for the selected date range</p>
              </div>
            ) : reportType === 'sales' && salesReport && (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600">{salesReport.totalOrders}</h3>
                        <p className="text-gray-600">Total Orders</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-purple-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-purple-600">₹{salesReport.totalSales?.toLocaleString()}</h3>
                        <p className="text-gray-600">Total Sales</p>
                        {(salesReport.totalCashSales > 0 || salesReport.totalOnlineSales > 0) && (
                          <div className="text-xs mt-2 space-y-1">
                            {salesReport.totalCashSales > 0 && (
                              <div className="text-green-600 font-medium">💵 Cash: ₹{salesReport.totalCashSales?.toLocaleString() || 0}</div>
                            )}
                            {salesReport.totalOnlineSales > 0 && (
                              <div className="text-blue-600 font-medium">💳 Online: ₹{salesReport.totalOnlineSales?.toLocaleString() || 0}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-orange-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-orange-600">₹{salesReport.totalGST?.toLocaleString()}</h3>
                        <p className="text-gray-600">Total GST</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Orders by Type */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Orders by Type</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-green-700">{salesReport.ordersByType['dine-in']}</h3>
                        <p className="text-green-700">Dine-in Orders</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-blue-700">{salesReport.ordersByType['delivery']}</h3>
                        <p className="text-blue-700">Delivery Orders</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-purple-700">{salesReport.ordersByType['pickup']}</h3>
                        <p className="text-purple-700">Pickup Orders</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-orange-700">{salesReport.ordersByType['vehicle']}</h3>
                        <p className="text-orange-700">Vehicle Orders</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Orders Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">GST Number</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {salesReport.orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((order) => (
                          <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{order.orderNumber}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{new Date(order.createdAt).toLocaleDateString()}</td>
                            <td className="px-6 py-4">
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">{order.customerId?.fullName || 'Guest'}</div>
                                {order.isMc && (
                                  <div className="mt-1">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      MC
                                    </span>
                                  </div>
                                )}
                                {order.complimentary && (
                                  <div className="mt-1 text-xs text-gray-600 max-w-[200px] truncate" title={order.complimentary}>
                                    🎁 {order.complimentary}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                {order.orderType}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {order.gstNumber ? (
                                <span className="text-xs font-medium text-green-600">
                                  {order.gstNumber}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                <div className="font-bold text-gray-900">₹{order.pricing?.total || 0}</div>
                                <div className="text-xs text-gray-500">
                                  GST: ₹{order.pricing?.gstAmount || 0}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Add other report types similarly... */}
            {reportType === 'items' && (!itemsReport || itemsReport.totalItems === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiPackage className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No items sold</h3>
                <p className="text-gray-600 mb-6">No items sold in the selected date range</p>
              </div>
            ) : reportType === 'items' && itemsReport && (
              <>
                {/* Items Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <FiPackage className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600">{itemsReport.totalItems}</h3>
                        <p className="text-gray-600">Total Items</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <FiShoppingCart className="text-green-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-green-600">{itemsReport.totalQuantity}</h3>
                        <p className="text-gray-600">Total Quantity</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Sub Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity Sold</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Revenue</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">% of Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {itemsReport.items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{item.itemName}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.category}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{item.subCategory}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">₹{item.price?.toLocaleString()}</td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {item.quantitySold}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{item.totalRevenue?.toLocaleString()}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="text-sm text-gray-900">{item.percentageOfTotal?.toFixed(1)}%</div>
                                <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full" 
                                    style={{ width: `${Math.min(item.percentageOfTotal, 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {reportType === 'purchase' && (!purchaseReport || purchaseReport.totalPurchases === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No purchase data available</h3>
                <p className="text-gray-600 mb-6">Select a date range to view purchase report</p>
              </div>
            ) : reportType === 'purchase' && purchaseReport && (
              <>
                {/* Purchase Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">💰</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-green-600">{purchaseReport.totalPurchases || 0}</h3>
                        <p className="text-gray-600">Total Purchases</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">💵</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600">₹{purchaseReport.totalAmount?.toLocaleString() || 0}</h3>
                        <p className="text-gray-600">Total Amount</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Purchases Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost Per Unit</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {purchaseReport.purchases && purchaseReport.purchases.length > 0 ? (
                          purchaseReport.purchases.map((purchase, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {new Date(purchase.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-gray-900">{purchase.itemId?.name || 'N/A'}</div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                  {purchase.itemId?.categoryId?.name || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-gray-900">{purchase.quantity}</div>
                              </td>
                              <td className="px-6 py-4 text-gray-700">₹{purchase.costPerUnit?.toLocaleString() || 0}</td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-green-600">₹{purchase.totalCost?.toLocaleString() || 0}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-600 max-w-xs truncate" title={purchase.notes}>
                                  {purchase.notes || '-'}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="px-6 py-12 text-center">
                              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl text-gray-400">💰</span>
                              </div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">No purchases found</h3>
                              <p className="text-gray-600">No purchase records found for the selected date range</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination Controls */}
                {purchaseReport.purchases && purchaseReport.purchases.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, purchaseReport.totalPurchases || 0)} of{' '}
                        {purchaseReport.totalPurchases || 0} purchases
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage <= 1}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Previous
                        </button>
                        
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage)) }, (_, i) => {
                            const totalPages = Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage);
                            const startPage = Math.max(1, currentPage - 2);
                            const endPage = Math.min(totalPages, startPage + 4);
                            const page = startPage + i;
                            
                            if (page <= totalPages) {
                              return (
                                <button
                                  key={page}
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-3 py-1 text-sm border rounded-md ${
                                    currentPage === page
                                      ? 'bg-blue-600 text-white border-blue-600'
                                      : 'border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            }
                            return null;
                          })}
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage), prev + 1))}
                          disabled={currentPage >= Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {reportType === 'users' && (!usersReport || usersReport.totalCustomers === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No users found</h3>
                <p className="text-gray-600 mb-6">No users found in the selected date range</p>
              </div>
            ) : reportType === 'users' && usersReport && (
              <>
                {/* Users Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600">{usersReport.totalCustomers}</h3>
                        <p className="text-gray-600">Total Customers</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-green-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-green-600">{usersReport.totalOrders}</h3>
                        <p className="text-gray-600">Total Orders</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <span className="text-purple-600 text-xl font-bold">₹</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-purple-600">₹{usersReport.totalRevenue?.toLocaleString()}</h3>
                        <p className="text-gray-600">Total Revenue</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <FiCalendar className="text-orange-600 text-xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-orange-600">₹{usersReport.averageRevenuePerCustomer?.toLocaleString()}</h3>
                        <p className="text-gray-600">Avg Revenue/Customer</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-green-50 to-green-100 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Phone</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Orders</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Revenue</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Avg Order</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order Types</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Visits</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Last Visit</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {usersReport.users.map((user, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">{user.customerName}</div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{user.customerPhone}</td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {user.totalOrders}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{user.totalRevenue?.toLocaleString()}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">₹{user.averageOrderValue?.toLocaleString()}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              <div className="flex gap-1 flex-wrap">
                                {user.orderTypes?.map((orderType, idx) => (
                                  <span key={idx} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                    {orderType.typeName}: {orderType.count}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {user.totalVisits}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {user.lastVisit ? new Date(user.lastVisit).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {reportType === 'rooms' && (!roomsReport || roomsReport.totalBookings === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No checkout data available</h3>
                <p className="text-gray-600 mb-6">Select a date range to view room checkout report</p>
              </div>
            ) : reportType === 'rooms' && roomsReport && (
              <>
                {/* Rooms Summary */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6">

                  {/* Total Bookings */}
                  <div className="bg-white rounded-lg border p-4 flex flex-col justify-between h-full">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center text-lg">
                        📅
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-yellow-600 leading-none">
                          {roomsReport.totalBookings || 0}
                        </h3>
                        <p className="text-xs text-gray-500">Total Bookings</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
                        <span className="text-gray-600">Booked: {roomsReport.totalBookedRooms || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        <span className="text-gray-600">Check-in: {roomsReport.totalCheckedInRooms || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        <span className="text-gray-600">Check-out: {roomsReport.totalCheckedOutRooms || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                        <span className="text-gray-600">Cancelled: {roomsReport.totalCancelledRooms || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Room Revenue & GST */}
                  <div className="bg-white rounded-lg border p-4 flex flex-col justify-between h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-lg">
                        💰
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-purple-600 leading-none">
                          ₹{(roomsReport.totalRoomRevenue || 0).toLocaleString('en-IN')}
                        </h3>
                        <p className="text-xs text-gray-500">Room Revenue</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-lg">
                        📋
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-green-600 leading-none">
                          ₹{(roomsReport.totalGSTAmount || 0).toLocaleString('en-IN')}
                        </h3>
                        <p className="text-xs text-gray-500">GST</p>
                      </div>
                    </div>
                  </div>

                  {/* Other & Total Revenue */}
                  <div className="bg-white rounded-lg border p-4 flex h-full">
                    {/* Other & Total Revenue - Left Side */}
                    <div className="flex flex-col justify-center pr-4">
                      {/* <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-lg">
                          ➕
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-orange-600 leading-none">
                            ₹{(roomsReport.totalOtherAmount || 0).toLocaleString('en-IN')}
                          </h3>
                          <p className="text-xs text-gray-500">Other</p>
                        </div>
                      </div> */}

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-lg">
                          💵
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-indigo-600 leading-none">
                            ₹{(roomsReport.totalRoomRevenue + roomsReport.totalGSTAmount + roomsReport.totalOtherAmount || 0).toLocaleString('en-IN')}
                          </h3>
                          <p className="text-xs text-gray-500">Total Revenue</p>
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="w-px bg-gray-200 mx-2"></div>

                    {/* Payment Method Breakdown - Right Side */}
                    <div className="flex-1 pl-4">
                      <div className="text-xs font-medium text-gray-700 mb-2">Payment Methods</div>
                      <div className="space-y-1">
                        {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.cash || 0), 0) || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Cash:</span>
                            <span className="text-xs font-semibold text-gray-800">
                              ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.cash || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.card || 0), 0) || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">Card:</span>
                            <span className="text-xs font-semibold text-gray-800">
                              ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.card || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.upi || 0), 0) || 0) > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600">UPI:</span>
                            <span className="text-xs font-semibold text-gray-800">
                              ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.upi || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                </div>

                {/* Rooms Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Details</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest & Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stay Period</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Other Amount</th> */}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adjustments</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Final Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {roomsReport.roomDetails && roomsReport.roomDetails.length > 0 ? (
                          roomsReport.roomDetails.map((room, index) => (
                            <tr key={index} className={`hover:bg-gray-50 transition-colors duration-150 ${
                              room.status === 'cancelled' ? 'bg-red-50 border-l-4 border-red-500' : ''
                            }`}>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-medium text-gray-900">{room.roomNumber}</div>
                                  <div className="text-sm text-gray-500">{room.roomType || 'Standard'}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="font-medium text-gray-900">{room.guestName || '-'}</div>
                                  <div className="mt-1">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      room.status === 'checked_out' 
                                        ? 'bg-green-100 text-green-800' 
                                        : room.status === 'cancelled'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}>
                                      {room.status?.replace('_', ' ') || 'checked_out'}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                <div>
                                  <div>{room.checkIn ? new Date(room.checkIn).toLocaleDateString() : '-'}</div>
                                  <div className="text-xs text-gray-500">to</div>
                                  <div>{room.checkOut ? new Date(room.checkOut).toLocaleDateString() : '-'}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-purple-600">₹{(room.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-medium text-green-600">₹{(room.gstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </td>
                              {/* <td className="px-6 py-4">
                                <div className="font-medium text-orange-600">₹{(room.otherAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </td> */}
                              <td className="px-6 py-4">
                                <div className="text-sm">
                                  <div className="font-medium text-blue-600">
                                    {(room.tipAmount || 0) > 0 
                                      ? `₹${(room.tipAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : `${(room.tipAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    }
                                  </div>
                                  <div className="font-medium text-red-600">
                                    {(room.discountAmount || 0) > 0 
                                      ? `-₹${(room.discountAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : `${(room.discountAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    }
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">Adjustments</div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="font-bold text-indigo-600">₹{(room.totalFinalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="space-y-2">
                                  <div className="font-bold text-green-600 text-sm">
                                    ₹{(room.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </div>
                                  {room.paymentModeBreakdown && (
                                    <div className="space-y-1 border-t pt-1">
                                      {room.paymentModeBreakdown.cash > 0 && (
                                        <div className="flex justify-between items-center text-xs">
                                          <span className="text-gray-600 font-medium">Cash:</span>
                                          <span className="text-gray-800 font-semibold">
                                            ₹{room.paymentModeBreakdown.cash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                      )}
                                      {room.paymentModeBreakdown.card > 0 && (
                                        <div className="flex justify-between items-center text-xs">
                                          <span className="text-gray-600 font-medium">Card:</span>
                                          <span className="text-gray-800 font-semibold">
                                            ₹{room.paymentModeBreakdown.card.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                      )}
                                      {room.paymentModeBreakdown.upi > 0 && (
                                        <div className="flex justify-between items-center text-xs">
                                          <span className="text-gray-600 font-medium">UPI:</span>
                                          <span className="text-gray-800 font-semibold">
                                            ₹{room.paymentModeBreakdown.upi.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    room.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                                    room.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {room.paymentStatus || 'pending'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                              No room details available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Banquet Report */}
            {reportType === 'banquet' && (!banquetReport || banquetReport.totalPackages === 0) ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCalendar className="text-gray-400 text-3xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No banquet data available</h3>
                <p className="text-gray-600 mb-6">Select a date range to view banquet sales report</p>
              </div>
            ) : reportType === 'banquet' && banquetReport && (
              <>
                {/* Banquet Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">🎊</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-blue-600">{banquetReport.totalPackages || 0}</h3>
                        <p className="text-gray-600">Total Packages</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">💰</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-green-600">₹{(banquetReport.totalRevenue || 0).toLocaleString('en-IN')}</h3>
                        <p className="text-gray-600">Total Revenue</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">📊</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-orange-600">{banquetReport.avgGstPercentage || 0}%</h3>
                        <p className="text-gray-600">Avg GST %</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <span className="text-xl">💵</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-purple-600">₹{Number(banquetReport.avgPricePerPack || 0).toLocaleString('en-IN')}</h3>
                        <p className="text-gray-600">Avg Price/Pack</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Banquet Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill & Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Details</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banquet Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Packages & Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST %</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {banquetReport.banquets?.map((banquet, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                <div className="text-sm font-medium text-gray-900">{banquet.billNo || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{new Date(banquet.createdAt).toLocaleDateString('en-IN')}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-start space-y-2">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 text-xs font-bold">👤</span>
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-900 text-sm">{banquet.customerName || 'N/A'}</div>
                                    {banquet.gstNumber && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                          GST: {banquet.gstNumber}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{banquet.banquetName || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-900">{banquet.noOfPacks || 0}</span>
                                  <span className="text-xs text-gray-500">×</span>
                                  <span className="text-sm font-semibold text-blue-600">₹{(banquet.pricePerPack || 0).toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  Total: ₹{((banquet.noOfPacks || 0) * (banquet.pricePerPack || 0)).toFixed(2)}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">₹{(banquet.subtotal || 0).toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{banquet.gstPercentage || 0}%</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">₹{(banquet.gstAmount || 0).toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-green-600">-₹{(banquet.discountAmount || 0).toFixed(2)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-bold text-blue-600">₹{(banquet.total || 0).toFixed(2)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default VendorOwnerReports;
